import { Vector3 } from 'three';
import type { LevelCollider } from '../collision/LevelCollider';
import { applyDamage, tickRespawn } from './health';
import type { CombatantDef, CombatantState, DamageEvent, ProjectileSpec } from './types';

interface Combatant {
  def: CombatantDef;
  state: CombatantState;
}

interface Projectile {
  spec: ProjectileSpec;
  position: Vector3;
  traveled: number;
}

/** Read-only render mirror of a live projectile. */
export interface ProjectileView {
  position: Vector3;
  radius: number;
  color: number;
}

/**
 * The combat simulation (spec 0025, ADR 0007). Owns all combatant state and
 * in-flight projectiles; scene-free so it runs headless in vitest. Rules:
 * advance only from the fixed-tick loop, no randomness, iteration/tie-break
 * order is registration order. `drainEvents()` is the single seam to the
 * presentation layer (VFX / damage numbers / HUD) — the sim never touches it.
 */
export class CombatWorld {
  private readonly combatants: Combatant[] = [];
  private readonly projectiles: Projectile[] = [];
  private events: DamageEvent[] = [];
  private collider: LevelCollider | null = null;

  // Scratch objects — the tick allocates nothing per substep.
  private readonly segStart = new Vector3();
  private readonly segEnd = new Vector3();
  private readonly closest = new Vector3();

  setCollider(collider: LevelCollider | null): void {
    this.collider = collider;
  }

  register(def: CombatantDef): void {
    this.combatants.push({
      def,
      state: { health: def.maxHealth, alive: true, respawnIn: 0 },
    });
  }

  state(id: string): CombatantState | undefined {
    return this.find(id)?.state;
  }

  /** Restores a combatant to full health/alive (practice-mode R restart). */
  resetHealth(id: string): void {
    const c = this.find(id);
    if (c) {
      c.state.health = c.def.maxHealth;
      c.state.alive = true;
      c.state.respawnIn = 0;
    }
  }

  /** Removes all combatants, projectiles, and pending events (map switch). */
  clear(): void {
    this.combatants.length = 0;
    this.projectiles.length = 0;
    this.events = [];
  }

  /** Direct damage (no travel, no dodge window — use sparingly). */
  applyDamage(sourceId: string, targetId: string, amount: number): void {
    const target = this.find(targetId);
    if (!target) return;
    this.damage(sourceId, target, amount, this.capsuleMid(target));
  }

  spawnProjectile(spec: ProjectileSpec): void {
    this.projectiles.push({ spec, position: spec.origin.clone(), traveled: 0 });
  }

  /**
   * Instant sphere strike at `center` against every living enemy of
   * `sourceId`'s team (capsule-vs-sphere, registration order). Returns hits.
   */
  strikeArea(sourceId: string, center: Vector3, radius: number, damage: number): number {
    const source = this.find(sourceId);
    if (!source) return 0;
    let hits = 0;
    for (const target of this.combatants) {
      if (!target.state.alive || target.def.team === source.def.team) continue;
      if (this.capsuleDistance(target, center) < radius + target.def.radius) {
        this.damage(sourceId, target, damage, this.capsuleMid(target));
        hits++;
      }
    }
    return hits;
  }

  /** Advances projectiles and respawn timers by one fixed tick. */
  tick(dt: number): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];
      if (projectile && !this.stepProjectile(projectile, dt)) {
        this.projectiles.splice(i, 1);
      }
    }
    for (const c of this.combatants) {
      tickRespawn(c.state, c.def.maxHealth, dt);
    }
  }

  /** Returns and clears this tick's damage events (call exactly once per tick). */
  drainEvents(): DamageEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  /** Live projectiles for the render-side visual mirror. */
  get projectileViews(): readonly ProjectileView[] {
    return this.projectiles.map((p) => ({
      position: p.position,
      radius: p.spec.radius,
      color: p.spec.color,
    }));
  }

  // ── internals ──────────────────────────────────────────────────────────

  private find(id: string): Combatant | undefined {
    return this.combatants.find((c) => c.def.id === id);
  }

  private damage(sourceId: string, target: Combatant, amount: number, position: Vector3): void {
    const result = applyDamage(target.state, amount);
    if (result.applied <= 0) return;
    if (result.lethal && target.def.respawnSec > 0) {
      target.state.respawnIn = target.def.respawnSec;
    }
    this.events.push({
      sourceId,
      targetId: target.def.id,
      amount: result.applied,
      position: position.clone(),
      lethal: result.lethal,
    });
  }

  /**
   * Advances one projectile by `dt` in substeps no longer than its radius
   * (anti-tunneling, same discipline as PlayerController). Returns false when
   * the projectile hit something or expired.
   */
  private stepProjectile(p: Projectile, dt: number): boolean {
    const { spec } = p;
    let remaining = Math.min(spec.speed * dt, spec.maxDistance - p.traveled);
    while (remaining > 0) {
      const step = Math.min(remaining, spec.radius);
      // Level hit: cast from the current position along the travel direction.
      if (this.collider) {
        const wall = this.collider.raycastFirst(p.position, spec.direction, step + spec.radius);
        if (wall !== null && wall <= step) {
          return false; // absorbed by the level
        }
      }
      p.position.addScaledVector(spec.direction, step);
      p.traveled += step;
      remaining -= step;
      // Body hit: capsule test against living enemies, registration order.
      const owner = this.find(spec.ownerId);
      for (const target of this.combatants) {
        if (!target.state.alive) continue;
        if (owner && target.def.team === owner.def.team) continue;
        if (this.capsuleDistance(target, p.position) < spec.radius + target.def.radius) {
          this.damage(spec.ownerId, target, spec.damage, p.position);
          return false;
        }
      }
    }
    return p.traveled < spec.maxDistance;
  }

  /** Distance from a point to the combatant's capsule core segment. */
  private capsuleDistance(c: Combatant, point: Vector3): number {
    const feet = c.def.getPosition();
    this.segStart.set(feet.x, feet.y + c.def.radius, feet.z);
    this.segEnd.set(feet.x, feet.y + c.def.height - c.def.radius, feet.z);
    // Closest point on the segment to `point` (segments are vertical).
    const t = Math.min(
      1,
      Math.max(0, (point.y - this.segStart.y) / Math.max(1e-9, this.segEnd.y - this.segStart.y)),
    );
    this.closest.copy(this.segStart).lerp(this.segEnd, t);
    return this.closest.distanceTo(point);
  }

  private capsuleMid(c: Combatant): Vector3 {
    const feet = c.def.getPosition();
    return new Vector3(feet.x, feet.y + c.def.height / 2, feet.z);
  }
}
